import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const [step, setStep] = useState(tokenFromUrl ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(tokenFromUrl || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
        setToken(res.data.reset_token);
      }
      setStep('sent');
      toast.success('Reset instructions generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setStep('done');
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/login')}
          className="mb-8"
          data-testid="back-to-login-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
        </Button>

        <div className="bg-white rounded-xl border border-border p-8 shadow-sm">
          {step === 'request' && (
            <>
              <div className="mb-8 text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-primary" />
                </div>
                <h1 className="text-2xl font-heading font-bold text-primary mb-2" data-testid="forgot-password-title">
                  Forgot Password?
                </h1>
                <p className="text-muted-foreground text-sm">
                  Enter your email and we'll generate a reset token for you.
                </p>
              </div>
              <form onSubmit={handleForgotPassword} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2"
                    data-testid="forgot-email-input"
                    placeholder="you@example.com"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11"
                  data-testid="request-reset-button"
                >
                  {loading ? 'Processing...' : 'Get Reset Token'}
                </Button>
              </form>
              <div className="mt-5 text-center text-sm">
                <span className="text-muted-foreground">Remember your password? </span>
                <Link to="/login" className="text-secondary font-medium hover:underline">
                  Sign in
                </Link>
              </div>
            </>
          )}

          {step === 'sent' && (
            <>
              <div className="mb-6 text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
                <h1 className="text-2xl font-heading font-bold text-primary mb-2" data-testid="token-sent-title">
                  Reset Token Generated
                </h1>
                <p className="text-muted-foreground text-sm">
                  In production, this would be emailed. For now, use the token below:
                </p>
              </div>
              {resetToken && (
                <div className="bg-slate-50 border rounded-lg p-3 mb-5">
                  <p className="text-xs text-muted-foreground mb-1">Your reset token:</p>
                  <p className="font-mono text-xs break-all text-primary font-medium" data-testid="reset-token-display">{resetToken}</p>
                </div>
              )}
              <Button
                onClick={() => setStep('reset')}
                className="w-full h-11"
                data-testid="proceed-to-reset-button"
              >
                <KeyRound className="w-4 h-4 mr-2" /> Set New Password
              </Button>
            </>
          )}

          {step === 'reset' && (
            <>
              <div className="mb-8 text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-7 h-7 text-primary" />
                </div>
                <h1 className="text-2xl font-heading font-bold text-primary mb-2" data-testid="set-password-title">
                  Set New Password
                </h1>
                <p className="text-muted-foreground text-sm">Enter your reset token and choose a new password.</p>
              </div>
              <form onSubmit={handleResetPassword} className="space-y-5" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="token">Reset Token</Label>
                  <Input
                    id="token"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    required
                    className="mt-2 font-mono text-sm"
                    data-testid="reset-token-input"
                    placeholder="Paste your reset token here"
                  />
                </div>
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="mt-2"
                    data-testid="new-password-input"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="mt-2"
                    data-testid="confirm-password-input"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11"
                  data-testid="submit-reset-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
              <div className="mt-4 text-center text-sm">
                <button
                  onClick={() => setStep('request')}
                  className="text-secondary text-sm hover:underline"
                  data-testid="back-to-request-link"
                >
                  Request a new token
                </button>
              </div>
            </>
          )}

          {step === 'done' && (
            <div className="text-center py-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-primary mb-2" data-testid="reset-success-title">
                Password Updated!
              </h1>
              <p className="text-muted-foreground text-sm mb-6">
                Your password has been reset successfully. You can now log in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-11"
                data-testid="go-to-login-button"
              >
                Go to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
